import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, Check, X, Heart, Apple, Dumbbell, Moon, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Gender emoji icons for inclusive display
const GENDER_ICONS = {
  female: "♀️",
  male: "♂️",
  trans_female: "⚧️",
  trans_male: "⚧️",
  non_binary: "⚧",
  genderfluid: "🌊",
  agender: "○",
  other: "✦",
  prefer_not_say: "🔒"
};

// Section component for collapsible sections
const ProfileSection = ({ title, icon: Icon, iconColor, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-white/10 rounded-lg overflow-hidden">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-sm font-rajdhani text-white">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 space-y-3 bg-black/20">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const WellnessProfileWidget = () => {
  const [expanded, setExpanded] = useState(false);
  const [profile, setProfile] = useState(null);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state with all new fields
  const [formData, setFormData] = useState({
    age_range: null,
    gender: null,
    activity_level: null,
    sleep_hours: null,
    goals: [],
    eating_habits: null,
    health_conditions: [],
    tracks_cycle: false,
    cycle_phase: null,
    dietary_restrictions: [],
    preferred_exercises: [],
    stress_level: null
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/wellness/profile`);
      setProfile(response.data.profile);
      setOptions(response.data.options);
      if (response.data.profile) {
        setFormData({
          age_range: response.data.profile.age_range || null,
          gender: response.data.profile.gender || null,
          activity_level: response.data.profile.activity_level || null,
          sleep_hours: response.data.profile.sleep_hours || null,
          goals: response.data.profile.goals || [],
          eating_habits: response.data.profile.eating_habits || null,
          health_conditions: response.data.profile.health_conditions || [],
          tracks_cycle: response.data.profile.tracks_cycle || false,
          cycle_phase: response.data.profile.cycle_phase || null,
          dietary_restrictions: response.data.profile.dietary_restrictions || [],
          preferred_exercises: response.data.profile.preferred_exercises || [],
          stress_level: response.data.profile.stress_level || null
        });
      }
    } catch (error) {
      console.error("Error fetching wellness profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/wellness/profile`, formData);
      setProfile(formData);
      toast.success("Perfil de bienestar guardado");
      setExpanded(false);
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const deleteProfile = async () => {
    try {
      await axios.delete(`${API}/wellness/profile`);
      setProfile(null);
      setFormData({
        age_range: null,
        gender: null,
        activity_level: null,
        sleep_hours: null,
        goals: [],
        eating_habits: null,
        health_conditions: [],
        tracks_cycle: false,
        cycle_phase: null,
        dietary_restrictions: [],
        preferred_exercises: [],
        stress_level: null
      });
      toast.success("Perfil eliminado");
    } catch (error) {
      console.error("Error deleting profile:", error);
    }
  };

  const toggleArrayField = (field, value) => {
    const currentArray = formData[field] || [];
    if (currentArray.includes(value)) {
      setFormData({ ...formData, [field]: currentArray.filter(v => v !== value) });
    } else {
      setFormData({ ...formData, [field]: [...currentArray, value] });
    }
  };

  const hasProfile = profile && (profile.age_range || profile.gender || profile.eating_habits);

  // Calculate profile completion percentage
  const getProfileCompletion = () => {
    const fields = ['age_range', 'gender', 'activity_level', 'eating_habits', 'goals'];
    const filled = fields.filter(f => formData[f] && (Array.isArray(formData[f]) ? formData[f].length > 0 : true)).length;
    return Math.round((filled / fields.length) * 100);
  };

  if (loading) {
    return (
      <div className="p-4 rounded-xl bg-white/5 border border-white/10 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-2/3 mb-2" />
        <div className="h-4 bg-white/10 rounded w-1/2" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
    >
      <div 
        className={`p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 cursor-pointer transition-all hover:border-pink-500/40 ${expanded ? 'rounded-b-none' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-pink-500/20">
              <Heart className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <h3 className="font-rajdhani font-semibold text-white text-sm">
                Perfil de Bienestar Personalizado
              </h3>
              <p className="text-xs text-slate-400">
                {hasProfile 
                  ? `${getProfileCompletion()}% completado • ${options?.genders?.[profile?.gender] || ''} ${profile?.age_range ? `• ${profile.age_range}` : ''}`.trim()
                  : "Configura tu perfil para recomendaciones personalizadas"
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasProfile && (
              <span className="px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-400 text-xs">
                Activo
              </span>
            )}
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && options && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 bg-black/40 border border-t-0 border-white/10 rounded-b-xl space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-xs text-slate-500 italic flex items-center gap-2">
                <AlertCircle className="w-3 h-3" />
                Toda la información es opcional y anónima. Cuanto más completes, mejores serán tus recomendaciones.
              </p>

              {/* SECTION 1: Basic Info */}
              <ProfileSection title="Información Básica" icon={Heart} iconColor="text-pink-400" defaultOpen={true}>
                {/* Age Range */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">Rango de edad</label>
                  <div className="flex flex-wrap gap-2">
                    {options.age_ranges?.map((age) => (
                      <button
                        key={age}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...formData, age_range: formData.age_range === age ? null : age });
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani transition-all ${
                          formData.age_range === age
                            ? "bg-pink-500/20 border border-pink-500/50 text-pink-400"
                            : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                      >
                        {age}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">
                    Género <span className="text-slate-600">(para recomendaciones hormonales)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {options.genders && Object.entries(options.genders).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...formData, gender: formData.gender === key ? null : key });
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani transition-all flex items-center gap-1 ${
                          formData.gender === key
                            ? "bg-purple-500/20 border border-purple-500/50 text-purple-400"
                            : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                      >
                        <span>{GENDER_ICONS[key]}</span>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Stress Level */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">Nivel de estrés actual</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <button
                        key={level}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...formData, stress_level: formData.stress_level === level ? null : level });
                        }}
                        className={`w-10 h-10 rounded-lg text-sm font-rajdhani transition-all flex items-center justify-center ${
                          formData.stress_level === level
                            ? level <= 2 ? "bg-green-500/20 border border-green-500/50 text-green-400"
                              : level === 3 ? "bg-yellow-500/20 border border-yellow-500/50 text-yellow-400"
                              : "bg-red-500/20 border border-red-500/50 text-red-400"
                            : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-600 mt-1">1 = Muy bajo, 5 = Muy alto</p>
                </div>
              </ProfileSection>

              {/* SECTION 2: Diet & Nutrition */}
              <ProfileSection title="Alimentación" icon={Apple} iconColor="text-green-400">
                {/* Eating Habits */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">Tipo de alimentación</label>
                  <div className="flex flex-wrap gap-2">
                    {options.eating_habits && Object.entries(options.eating_habits).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...formData, eating_habits: formData.eating_habits === key ? null : key });
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani transition-all ${
                          formData.eating_habits === key
                            ? "bg-green-500/20 border border-green-500/50 text-green-400"
                            : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dietary Restrictions */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">
                    Restricciones alimentarias <span className="text-slate-600">(selecciona todas las que apliquen)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {options.dietary_restrictions && Object.entries(options.dietary_restrictions).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleArrayField('dietary_restrictions', key);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani transition-all flex items-center gap-1 ${
                          formData.dietary_restrictions?.includes(key)
                            ? "bg-orange-500/20 border border-orange-500/50 text-orange-400"
                            : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                      >
                        {formData.dietary_restrictions?.includes(key) && <Check className="w-3 h-3" />}
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </ProfileSection>

              {/* SECTION 3: Exercise */}
              <ProfileSection title="Ejercicio" icon={Dumbbell} iconColor="text-orange-400">
                {/* Activity Level */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">Nivel de actividad física actual</label>
                  <div className="flex flex-wrap gap-2">
                    {options.activity_levels && Object.entries(options.activity_levels).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...formData, activity_level: formData.activity_level === key ? null : key });
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani transition-all ${
                          formData.activity_level === key
                            ? "bg-orange-500/20 border border-orange-500/50 text-orange-400"
                            : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preferred Exercises */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">
                    Ejercicios preferidos <span className="text-slate-600">(selecciona los que te gusten)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {options.exercise_preferences && Object.entries(options.exercise_preferences).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleArrayField('preferred_exercises', key);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani transition-all flex items-center gap-1 ${
                          formData.preferred_exercises?.includes(key)
                            ? "bg-cyan-500/20 border border-cyan-500/50 text-cyan-400"
                            : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                      >
                        {formData.preferred_exercises?.includes(key) && <Check className="w-3 h-3" />}
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </ProfileSection>

              {/* SECTION 4: Health & Hormones */}
              <ProfileSection title="Salud y Hormonas" icon={Moon} iconColor="text-indigo-400">
                {/* Health Conditions */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">
                    Condiciones de salud <span className="text-slate-600">(opcional, para personalizar recomendaciones)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {options.health_conditions && Object.entries(options.health_conditions).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleArrayField('health_conditions', key);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani transition-all flex items-center gap-1 ${
                          formData.health_conditions?.includes(key)
                            ? "bg-indigo-500/20 border border-indigo-500/50 text-indigo-400"
                            : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                        }`}
                      >
                        {formData.health_conditions?.includes(key) && <Check className="w-3 h-3" />}
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cycle Tracking */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">
                    Seguimiento del ciclo menstrual
                  </label>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({ ...formData, tracks_cycle: !formData.tracks_cycle, cycle_phase: !formData.tracks_cycle ? formData.cycle_phase : null });
                    }}
                    className={`px-4 py-2 rounded-lg text-xs font-rajdhani transition-all flex items-center gap-2 ${
                      formData.tracks_cycle
                        ? "bg-pink-500/20 border border-pink-500/50 text-pink-400"
                        : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                    }`}
                  >
                    {formData.tracks_cycle ? <Check className="w-3 h-3" /> : null}
                    {formData.tracks_cycle ? "Sí, hago seguimiento" : "No aplica / No hago seguimiento"}
                  </button>
                </div>

                {/* Cycle Phase - Only show if tracking */}
                {formData.tracks_cycle && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <label className="text-xs text-slate-400 mb-2 block">Fase actual del ciclo</label>
                    <div className="flex flex-wrap gap-2">
                      {options.cycle_phases && Object.entries(options.cycle_phases).filter(([key]) => key !== 'not_applicable').map(([key, label]) => (
                        <button
                          key={key}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData({ ...formData, cycle_phase: formData.cycle_phase === key ? null : key });
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani transition-all ${
                            formData.cycle_phase === key
                              ? "bg-pink-500/20 border border-pink-500/50 text-pink-400"
                              : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </ProfileSection>

              {/* SECTION 5: Goals */}
              <ProfileSection title="Objetivos de Bienestar" icon={Heart} iconColor="text-cyan-400">
                <div className="flex flex-wrap gap-2">
                  {options.goals && Object.entries(options.goals).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleArrayField('goals', key);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani transition-all flex items-center gap-1 ${
                        formData.goals?.includes(key)
                          ? "bg-cyan-500/20 border border-cyan-500/50 text-cyan-400"
                          : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                      }`}
                    >
                      {formData.goals?.includes(key) && <Check className="w-3 h-3" />}
                      {label}
                    </button>
                  ))}
                </div>
              </ProfileSection>

              {/* Actions */}
              <div className="flex gap-2 pt-2 sticky bottom-0 bg-black/60 py-3 -mx-4 px-4 border-t border-white/10">
                {hasProfile && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteProfile();
                    }}
                    variant="outline"
                    size="sm"
                    className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Eliminar
                  </Button>
                )}
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    saveProfile();
                  }}
                  disabled={saving}
                  size="sm"
                  className="flex-1 bg-gradient-to-r from-pink-500/30 to-purple-500/30 border border-pink-500/50 text-pink-300 hover:from-pink-500/40 hover:to-purple-500/40"
                >
                  {saving ? "Guardando..." : "Guardar Perfil"}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
